/**
 * @module
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// tslint:disable:max-file-line-count
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
// Core Libraries
import { Output, EventEmitter } from '@angular/core';
import { Injectable } from '@angular/core';
import { Wove } from 'aspect.js/';
import { UntypedFormBuilder } from '@angular/forms';

// Services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { SupportOrderPaymentService } from '@support/payment-options/services/support-payment.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';

// Constants
import { RETURN_PAYMENT_CONSTANTS } from '@experience/shop/return-payment/return-payment-constants/return-payment.constants';
import { UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { ChangeEvent } from '@experience/repdash/interfaces/aem-content.interface';

// Interfaces
import { APIError } from '@support/api/common/error.interface';
import { Address } from '@support/api/proxies/responses/api-2004.interface';
import {
  RefundOptionsResponse,
  AllowedRefundMethod,
} from '@support/api/proxies/responses/api-519.interface';
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';
import { Cart, Payment } from '@support/api/proxies/responses/api-cart-response.interface';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { ReturnPaymentMethods } from '@experience/shop/return-payment/interfaces/return-payment.interface';
import { Api2604Model } from '@experience/shop/return-payment/models/api-2604.model';
import { SHARED_PAYMENT } from '@support/payment/shared-payment/constants/shared-payment.constants';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { InStoreCardDetails } from '@support/payment/instore-payment/models/instore-card-details.model';
import { PostOrderPayment } from '@support/payment/instore-payment/models/instore-post-order-payment-request.model';
import { throwError as observableThrowError, of as observableOf, Subject, Observable } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';

@Wove({ moduleName: RETURN_PAYMENT_CONSTANTS.RETURN_PAYMENT_MODULE })
@Injectable()
export class ReturnPaymentService {
  /**
   * stores the paymentMethod
   */
  public paymentMethod: string;
  /**
   * display modal
   */
  public displayModal: boolean;
  /**
   * return amount
   */
  public returnAmount: string;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Stores submit status
   */
  public onSubmitStatus: boolean;
  /**
   * To store on Change Event
   */
  @Output() public onChangeEvent: EventEmitter<ChangeEvent>;
  /**
   * emitter to update remaining amount
   */
  @Output() public updateRemainingAmount: EventEmitter<string>;

  /**
   * Validates the address
   */
  public addressValidator: EventEmitter<Object>;
  /**
   * paymentId
   */
  public paymentId: number;
  /**
   * paymentMethodCode
   */
  public paymentMethodCode: string;

  constructor(
    private apiGatewayService: ApiGatewayService,
    private objectService: ObjectService,
    private formCreationService: FormCreationService,
    private supportOrderPaymentService: SupportOrderPaymentService,
    private formBuilder: UntypedFormBuilder,
    private stateStoreUtilsService: StateStoreUtilsService,
    private cartSupportService: CartSupportService,
    private commonUtilService: CommonUtilService
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.returnAmount = '';
    this.onChangeEvent = new EventEmitter();
    this.updateRemainingAmount = new EventEmitter();
    this.addressValidator = new EventEmitter();
  }
  /**
   * Method is used to get Refund Options
   * @param {string} chargeAccountNumber
   * @returns {Observable<VestaToken>}
   */
  public getRefundOptions(): Observable<RefundOptionsResponse | HttpResponse<object>> {
    const payload = {
      type: ['REFUNDMETHODS'],
    };
    return this.apiGatewayService
      .postRequest(ASSISTED_API_MAP.API_519_getPaymentReferenceData, payload, null, null)
      .pipe(
        map((response: HttpResponse<RefundOptionsResponse>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return this.errorHandler(error);
        })
      );
  }
  /**
   * method to set cash payment amount
   * @param {Payment} paymentDetails
   * @returns string
   */
  public getCashPaymentAmount(paymentDetails: ReturnPaymentMethods[]): string {
    const returnAmountArray = paymentDetails.filter((payment: ReturnPaymentMethods) => {
      return payment.originalRefundMethod === RETURN_PAYMENT_CONSTANTS.CASH;
    });
    this.returnAmount =
      returnAmountArray.length > 0
        ? this.objectService.get(returnAmountArray[0], 'availableAmount')
        : '';
    return this.returnAmount;
  }
  /**
   * used to update cart by calling API 2604 if any change in refund Amount or refund method
   */
  public postCheckoutUpdate(refundPaymentMethod?: ReturnPaymentMethods[]): Observable<object> {
    if (
      this.isUpdateCheckoutRequired(refundPaymentMethod) ||
      this.isDefaultPaymentMethodOverrided(refundPaymentMethod)
    ) {
      return this.cancelCartCheckout().pipe(
        mergeMap(() => {
          const cart: Cart = this.stateStoreUtilsService.getCart();
          const pathParams = {
            cartId: cart.cartId,
          };
          const originalOrderId = this.stateStoreUtilsService.getCustomerOrderId();
          const requestPayload = new Api2604Model(
            cart.orderReason,
            cart.orderType,
            originalOrderId,
            refundPaymentMethod,
            this.objectService,
            this.stateStoreUtilsService,
            this
          );
          return this.apiGatewayService
            .postRequest(
              API_MAP.API_2604_updatePaymentMethodAddress,
              requestPayload,
              null,
              null,
              pathParams
            )
            .pipe(
              map((response: HttpResponse<Object>) => {
                this.cartSupportService.announceCartUpdate(
                  this.objectService.get(response.body, 'cart')
                );
                return response.body;
              }),
              catchError((error: APIError) => {
                return observableThrowError(error);
              })
            );
        })
      );
    } else {
      return observableOf(null);
    }
  }

  /**
   * This method is used to cancel cart checkout before modifying cart (ie. Calling API 2604)
   * @returns Observable
   */
  private cancelCartCheckout(): Observable<void> {
    const cartCheckedOutStatus = this.stateStoreUtilsService.getCartCheckedOutStatusFlag();
    if (cartCheckedOutStatus) {
      const removeCart = 'false';
      return this.cartSupportService.cancelCart(removeCart);
    } else {
      return observableOf(null);
    }
  }
  /**
   * method used to toggle payment method based on selection
   * @param allowedRefundMethod : AllowedRefundMethod[]
   * @param index1 : number
   * @param index2 : number
   * @param refundPaymentsMethods : ReturnPaymentMethods[]
   * @param originalPaymentMethod : string
   * @param isCardPaymentFailed : boolean
   * @returns void
   */
  public togglePaymentMethod(
    allowedRefundMethod: AllowedRefundMethod[],
    index1: number,
    index2: number,
    refundPaymentsMethods: ReturnPaymentMethods[],
    originalPaymentMethod?: string,
    isCardPaymentFailed?: boolean
  ): ReturnPaymentMethods[] {
    if (allowedRefundMethod) {
      if (allowedRefundMethod.length > 1) {
        if (index1 === 1) {
          // Method to toggle radio button When gift card option selected in case of split payment
          this.toggleGiftCardOption(
            allowedRefundMethod,
            index1,
            index2,
            refundPaymentsMethods,
            originalPaymentMethod,
            isCardPaymentFailed
          );
        } else {
          // Method to toggle radio button When cash tender option selected in case of split payment
          refundPaymentsMethods[index2].selectedRefundMethod =
            refundPaymentsMethods[index2].originalRefundMethod;
          if (refundPaymentsMethods[index2].selected) {
            if (originalPaymentMethod !== RETURN_PAYMENT_CONSTANTS.DEBIT) {
              if (!allowedRefundMethod[index1].isChecked) {
                allowedRefundMethod[index1].isChecked = !allowedRefundMethod[index1].isChecked;
                allowedRefundMethod[index1 + 1].isChecked =
                  !allowedRefundMethod[index1 + 1].isChecked;
                refundPaymentsMethods[index2].selected = !refundPaymentsMethods[index2].selected;
              }
            } else {
              allowedRefundMethod[index1].isChecked = !allowedRefundMethod[index1].isChecked;
              allowedRefundMethod[index1 + 1].isChecked =
                !allowedRefundMethod[index1 + 1].isChecked;
              refundPaymentsMethods[index2].selected = !refundPaymentsMethods[index2].selected;
            }
          }
          refundPaymentsMethods[index2].refundMethod.allowedRefundMethod[index1] = Object.create(
            allowedRefundMethod[index1]
          );
          refundPaymentsMethods[index2].refundMethod.allowedRefundMethod[index1 + 1] =
            Object.create(allowedRefundMethod[index1 + 1]);
        }
      } else {
        if (!allowedRefundMethod[index1].isChecked) {
          allowedRefundMethod[index1].isChecked = !allowedRefundMethod[index1].isChecked;
          refundPaymentsMethods[index2].selected = !refundPaymentsMethods[index2].selected;
        }
        refundPaymentsMethods[index2].refundMethod.allowedRefundMethod[index1] = Object.create(
          allowedRefundMethod[index1]
        );
      }
    }
    return refundPaymentsMethods;
  }
  /**
   * This Method is used to handling API error
   * @param {APIError} error
   * @returns {Observable<HttpResponse<object>>}
   */
  private errorHandler(error: APIError): Observable<HttpResponse<object>> {
    return observableThrowError(error);
  }
  /**
   * Function filters suggested addresses before returning data. The actual address is identified by
   * address1.overrideIndicator === 'true', hence it would sort to the first value on the list
   * @param addresses All suggested addresses from API_266 response
   * @returns SuggestedAddress as an array object
   */
  public filterSuggestedAddressData(addresses: Address[]): Address[] {
    return addresses.sort((address1: Address, address2: Address) => {
      // If address1 has overrideIndicator === 'true', then it should come first
      if (address1.overrideIndicator === 'true') {
        return address2.overrideIndicator === 'true' ? 0 : -1;
      } else {
        return address2.overrideIndicator === 'true' ? 1 : 0;
      }
    });
  }
  /**
   * Function used to create Form Array
   * @returns {FormGroup}
   * @param refundPaymentMethods: ReturnPaymentMethods[]
   */
  public createFormGroup(refundPaymentMethods: ReturnPaymentMethods[]): UntypedFormGroup {
    return this.formBuilder.group({
      refundForm: this.buildRefundAmountForm(refundPaymentMethods),
    });
  }
  /**
   * Method is used to build refund amount form
   * @returns {FormArray}
   * @param refundPaymentMethods: ReturnPaymentMethods[]
   */
  private buildRefundAmountForm(refundPaymentMethods: ReturnPaymentMethods[]): UntypedFormArray {
    const amountFormArray = this.formBuilder.array([]);
    refundPaymentMethods.forEach((refundPaymentMethod: ReturnPaymentMethods) => {
      amountFormArray.push(this.buildRefundPaymentMethodForm(refundPaymentMethod));
    });
    return amountFormArray;
  }
  /**
   * used to build refund payment amount form
   * @returns {FormGroup}
   * @param refundPaymentMethods: ReturnPaymentMethods
   */
  private buildRefundPaymentMethodForm(
    refundPaymentMethods: ReturnPaymentMethods
  ): UntypedFormGroup {
    const fieldValidationConstants = this.supportOrderPaymentService.getFieldValidationConstants();
    const debitAmountDetails =
      refundPaymentMethods.originalRefundMethod === RETURN_PAYMENT_CONSTANTS.DEBIT
        ? refundPaymentMethods.refundAmount
        : '';
    const creditAmountDetails =
      refundPaymentMethods.originalRefundMethod === RETURN_PAYMENT_CONSTANTS.CREDIT
        ? refundPaymentMethods.refundAmount
        : '';
    const cashAmountDetails =
      refundPaymentMethods.originalRefundMethod === RETURN_PAYMENT_CONSTANTS.CASH
        ? refundPaymentMethods.refundAmount
        : '';
    return this.formBuilder.group({
      debitRefundAmt: this.formCreationService.getFormControl(
        fieldValidationConstants.DOLLAR_AMOUNT,
        debitAmountDetails || '',
        true
      ),
      creditRefundAmt: this.formCreationService.getFormControl(
        fieldValidationConstants.DOLLAR_AMOUNT,
        creditAmountDetails || '',
        true
      ),
      cashRefundAmt: this.formCreationService.getFormControl(
        fieldValidationConstants.DOLLAR_AMOUNT,
        cashAmountDetails || '',
        true
      ),
    });
  }
  /*
   * @returns AddressRequest
   * method used to toggle payment method based on gift card selection
   * @param allowedRefundMethod : AllowedRefundMethod[]
   * @param index1 : number
   * @param index2 : number
   * @param refundPaymentsMethods : ReturnPaymentMethods[]
   * @params originalPaymentMethod : string
   * @params isCardPaymentFailed : boolean
   * @returns void
   */
  private toggleGiftCardOption(
    allowedRefundMethod: AllowedRefundMethod[],
    index1: number,
    index2: number,
    refundPaymentsMethods: ReturnPaymentMethods[],
    originalPaymentMethod?: string,
    isCardPaymentFailed?: boolean
  ): void {
    if (!isCardPaymentFailed) {
      if (!allowedRefundMethod[index1].isChecked) {
        allowedRefundMethod[index1].isChecked = !allowedRefundMethod[index1].isChecked;
        allowedRefundMethod[index1 - 1].isChecked = !allowedRefundMethod[index1 - 1].isChecked;
        refundPaymentsMethods[index2].selected = !refundPaymentsMethods[index2].selected;
      }
    } else {
      if (originalPaymentMethod !== RETURN_PAYMENT_CONSTANTS.DEBIT) {
        if (!allowedRefundMethod[index1].isChecked) {
          allowedRefundMethod[index1].isChecked = !allowedRefundMethod[index1].isChecked;
          allowedRefundMethod[index1 - 1].isChecked = !allowedRefundMethod[index1 - 1].isChecked;
          refundPaymentsMethods[index2].selected = !refundPaymentsMethods[index2].selected;
        }
      }
    }
    refundPaymentsMethods[index2].selectedRefundMethod = RETURN_PAYMENT_CONSTANTS.PREPAID;
    refundPaymentsMethods[index2].refundMethod.allowedRefundMethod[index1] = Object.create(
      allowedRefundMethod[index1]
    );
    refundPaymentsMethods[index2].refundMethod.allowedRefundMethod[index1 - 1] = Object.create(
      allowedRefundMethod[index1 - 1]
    );
  }
  /**
   * method to set dollar sign in front of amount
   * @param amount
   * @returns string
   */
  public getValue(amount: string): string {
    return this.commonUtilService.isNotEmpty(amount) ? amount.replace(/\`|\$|,|\s/g, '') : '0';
  }

  /**
   * used to make API 2150 call in exchange scenario
   * @param {ReturnPaymentMethods[]} returnPaymentMethods
   * @param {string} cartId
   * @param {boolean} isPostPaymentRequired
   * @return {Observable<Object>}
   */
  public postPaymentMethod(
    returnPaymentMethods: ReturnPaymentMethods[],
    cartId?: string,
    isPostPaymentRequired?: boolean,
    cardData?: InStoreCardDetails
  ): Observable<object> {
    if (returnPaymentMethods.length && isPostPaymentRequired) {
      return this.postPaymentForExchange(returnPaymentMethods, 0, cartId, cardData);
    } else {
      return observableOf(null);
    }
  }

  private isUpdateCheckoutRequired(refundPaymentMethods: ReturnPaymentMethods[]): boolean {
    return this.commonUtilService.isNotEmpty(
      refundPaymentMethods.find((refundPaymentMethod: ReturnPaymentMethods) => {
        return (
          refundPaymentMethod.selectedRefundMethod !== refundPaymentMethod.originalRefundMethod ||
          refundPaymentMethod.authorizationAmount.text !== refundPaymentMethod.refundAmount
        );
      })
    );
  }
  /**
   * Function to check default return payment method overrided
   * @param returnPaymentMethods
   * @returns boolean
   */
  public isDefaultPaymentMethodOverrided(refundPaymentMethods: ReturnPaymentMethods[]): boolean {
    const cart = this.stateStoreUtilsService.getCart();
    const defaultPaymentMethod = cart.payment.find(
      (payment: Payment) =>
        payment.OriginalPaymentMethod === RETURN_PAYMENT_CONSTANTS.CASH &&
        payment.paymentMethodCode === RETURN_PAYMENT_CONSTANTS.PREPAID
    );
    const selectedPaymentMethod = refundPaymentMethods.find(
      (refundPaymentMethod: ReturnPaymentMethods) =>
        refundPaymentMethod.originalRefundMethod === RETURN_PAYMENT_CONSTANTS.CASH
    );
    return (
      defaultPaymentMethod &&
      selectedPaymentMethod &&
      defaultPaymentMethod.paymentMethodCode !== selectedPaymentMethod.selectedRefundMethod
    );
  }
  /**
   * Post Payment Method for Exchange scenario
   * @param returnPaymentMethods
   * @param index
   * @param cartId
   * @param cardData
   */
  private postPaymentForExchange(
    returnPaymentMethods: ReturnPaymentMethods[],
    index: number,
    cartId?: string,
    cardData?: InStoreCardDetails
  ): Observable<object> {
    if (index < returnPaymentMethods.length) {
      const storeData = this.stateStoreUtilsService.getStoreData();
      this.paymentId = Math.floor(this.commonUtilService.generateRandomId());
      this.paymentMethodCode = this.objectService.get(
        returnPaymentMethods,
        `${index}.selectedRefundMethod`
      );
      let payload;
      if (
        this.objectService.get(returnPaymentMethods, `${index}.selectedRefundMethod`) !==
        RETURN_PAYMENT_CONSTANTS.DEBIT
      ) {
        payload = {
          actionCode: SHARED_PAYMENT.ADD,
          cartId: cartId,
          termsAndConditionsAccepted: true,
          isStoreDigital: storeData.isStoreDigital,
          paymentMethod: {
            posId: this.stateStoreUtilsService.getMacAddress(),
            paymentId: this.paymentId.toString(),
            receivedPaymentId: this.objectService.get(
              returnPaymentMethods,
              `${index}.receivedPaymentId`
            ),
            method: this.objectService.get(returnPaymentMethods, `${index}.selectedRefundMethod`),
            paymentMethodCode: this.objectService.get(
              returnPaymentMethods,
              `${index}.selectedRefundMethod`
            ),
            transactionType: SHARED_PAYMENT.REFUND,
            refundSequenceNo: this.objectService.get(
              returnPaymentMethods,
              `${index}.refundSequenceNo`
            ),
            amount: {
              currencyCode: SHARED_PAYMENT.CURRENCY,
              value: this.getValue(
                this.objectService.get(returnPaymentMethods, `${index}.refundAmount`)
              ),
            },
          },
        };
      } else {
        const cart = this.stateStoreUtilsService.getCart();
        payload = new PostOrderPayment(
          cardData,
          cart.cartId,
          cart.orderType,
          this.stateStoreUtilsService.getMacAddress(),
          storeData.address,
          storeData.isStoreDigital,
          null,
          this.objectService,
          cart
        );
        payload.setPaymentMethodValue('amount', {
          currencyCode: SHARED_PAYMENT.CURRENCY,
          value: this.getValue(
            this.objectService.get(returnPaymentMethods, `${index}.refundAmount`)
          ),
        });
        payload.setPaymentMethodValue('transactionType', SHARED_PAYMENT.REFUND);
        payload.setPaymentMethodValue('paymentId', this.paymentId);
        payload.setPaymentMethodValue(
          'receivedPaymentId',
          this.objectService.get(returnPaymentMethods, `${index}.receivedPaymentId`)
        );
        payload.setPaymentMethodValue(
          'refundSequenceNo',
          this.objectService.get(returnPaymentMethods, `${index}.refundSequenceNo`)
        );
      }
      const networkBarcode = this.stateStoreUtilsService.getNetworkBarcode();
      if (
        networkBarcode &&
        this.objectService.get(returnPaymentMethods, `${index}.selectedRefundMethod`) ===
          RETURN_PAYMENT_CONSTANTS.CASH
      ) {
        payload.paymentMethod.networkBarcode = networkBarcode;
      }
      return this.cartSupportService.commonSubmitOrder(payload).pipe(
        mergeMap(
          () => {
            return this.postPaymentForExchange(returnPaymentMethods, ++index, cartId, cardData);
          },
          (error: Object) => {
            return observableThrowError(error);
          }
        )
      );
    } else {
      return observableOf(null);
    }
  }
}
